import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { BRIEF_DEFAULTS, BRIEF_DEFAULT_FIELDS, BriefDefaultKey, mergeBriefDefaults } from '../briefTemplates';

// The standing instructions behind the [DEFAULT …] tokens in the brief templates.
//
// They are shared across every project, not per-project: the phrasing of "hold for
// about 4 seconds" or the house editing style is the same brief every time, and
// restating it in each project is how those sentences drift apart. Editing them here
// changes what every future brief says.
//
// Existing projects are untouched — a section is stored as the text it was seeded with,
// so changing a default reshapes new briefs rather than rewriting old ones.
interface Props {
  open: boolean;
  onClose: () => void;
  load: () => Promise<Partial<Record<BriefDefaultKey, string>> | null>;
  onSave: (values: Record<BriefDefaultKey, string>) => Promise<void>;
}

export default function BriefDefaultsDialog({ open, onClose, load, onSave }: Props) {
  const [values, setValues] = useState<Record<BriefDefaultKey, string>>(BRIEF_DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read through a ref so the fetch is tied to OPENING the dialog and nothing else.
  // The caller declares `load` inside its own component body, so it is a new function on
  // every one of its renders — and it re-renders often (the transcode-status poll ticks
  // every 2s while a clip prepares). With `load` in the dependency list, each of those
  // renders re-ran this effect and setValues() overwrote whatever the user was typing.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!open) return undefined;
    let stopped = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const stored = await loadRef.current();
        if (stopped) return;
        // A key that has never been saved — or was saved BLANK — falls back to the
        // built-in wording, so the dialog always shows what a brief would actually say
        // rather than a blank box. Same merge the panel and the renderer use, because
        // three readings of "what does this default say" is two too many.
        setValues(mergeBriefDefaults(stored));
      } catch (err: any) {
        if (!stopped) setError(err?.message || 'Could not load the saved defaults.');
      } finally {
        if (!stopped) setLoading(false);
      }
    })();
    return () => { stopped = true; };
  }, [open]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await onSave(values);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not save the defaults.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Brief defaults</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ fontSize: '0.85rem', mb: 2 }}>
          The standing wording every brief starts from. Changing it here changes what new
          projects say — projects you have already written stay as they are.
        </DialogContentText>
        {loading ? <CircularProgress size={20} /> : BRIEF_DEFAULT_FIELDS.map(({ key, label, token }) => (
          <div key={key}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              {label.toUpperCase()} — {token}
            </Typography>
            <TextField
              value={values[key] ?? ''}
              onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              size="small"
              sx={{ mt: 0.5, mb: 2 }}
              inputProps={{ 'aria-label': label }}
            />
          </div>
        ))}
        {error && <Typography variant="caption" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={loading || saving}>
          {saving ? 'Saving…' : 'Save defaults'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
